import {useEffect, useState} from "react";

import {useScoreValue} from "@hooks/ScoreValue";
import bar_icon from "@images/table/bar-icon.svg";
import line_icon from "@images/table/line-icon.svg";
import radarArrow from "@images/table/radar-arrow.png";
import radarBody from "@images/table/radar-body.png";
import radarChart from "@images/table/radar-chart.png";
import {
  getSessionsByMonth,
  getSessionsByScore,
  setChartsDate,
} from "@redux/slices/dashboardSlices/dashboardCallSlice";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {Spinner} from "@ui/general/Spinner";
import {WarningData} from "@ui/general/WarningData";
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import moment from "moment";
import {Chart, Doughnut} from "react-chartjs-2";
import CountUp from "react-countup";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {ChartLabel} from "../../components/ChartLabel";
import {Head} from "../../components/Head";
import styles from "./styles.module.scss";

ChartJS.register(
  ArcElement,
  Title,
  Filler,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
);

export const PerformanceCall = () => {
  const minDate = moment().subtract(10, "years").toDate();
  const [withArchived, setWithArchived] = useState(false);
  const {width, isMobile} = useSelector((state) => state.common.settings);
  const {
    date: chartsDate,
    status,
    error,
    data,
  } = useSelector((state) => state.dashboard.call.sessions);

  const dispatch = useDispatch();

  const chartLabels = data?.month?.labels || [];
  const chartSimulations = data?.month?.simulations || [];
  const chartAvgScore = data?.month?.avgScores || [];
  const totalSimulations = chartSimulations.reduce((a, b) => a + b, 0);

  const chartCutout = (width * 3.25) / 100;
  const chartFontSize = (width * 0.75) / 100;
  const chartPadding = (width * 0.5) / 100;
  const chartBorder = (width * 0.25) / 100;
  const margin = (width * 0.5) / 100;

  const {t} = useTranslation();

  const {currentScore} = useScoreValue();

  let doughnutLabels = [];
  let doughnutCounts = [];
  let doughnutColorsDynamic = [];

  if (data?.score?.data) {
    const scores = data?.score.data.filter((item) => item.score);

    for (let i = 1; i <= 5; i++) {
      const found = scores.find((item) => item.score === i);
      const scoreObj = currentScore(i);

      doughnutLabels.push(scoreObj.grade);
      doughnutCounts.push(found ? found.count : 0);
      doughnutColorsDynamic.push(scoreObj.color);
    }
  }

  doughnutLabels = doughnutLabels.reverse();
  doughnutCounts = doughnutCounts.reverse();
  doughnutColorsDynamic = doughnutColorsDynamic.reverse();

  const doughnutLabelsList = [
    {id: 1, name: t("page.dashboard.simulations_count"), icon: line_icon},
    {id: 2, name: t("page.dashboard.average_score"), icon: bar_icon},
  ];

  const doughnutData = {
    labels: doughnutLabels,
    datasets: [
      {
        data: doughnutCounts,
        backgroundColor: doughnutColorsDynamic,
        borderRadius: margin,
        borderWidth: 0,
        hoverOffset: 0,
        offset: [0, 0],
      },
    ],
  };

  const rootData = {
    labels: chartLabels,
    datasets: [
      {
        type: "line",
        label: t("page.dashboard.simulations_count"),
        borderColor: "#8692CB",
        pointBackgroundColor: "#8692CB",
        backgroundColor: "rgba(134, 146, 203, 0.15)",
        tension: 0.2,
        fill: true,
        data: chartSimulations,
        yAxisID: "y",
      },
      {
        type: "bar",
        label: t("page.dashboard.average_score"),
        backgroundColor: "#8A6ACB",
        hoverBackgroundColor: "#B262C9",
        borderColor: "transparent",
        data: chartAvgScore,
        borderRadius: margin,
        borderWidth: 2,
        barPercentage: isMobile ? 1 : 0.5,
        yAxisID: "y1",
      },
    ],
  };

  const rootOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 3000,
    },
    elements: {
      line: {
        borderWidth: width >= 1920 || isMobile ? 5 : chartBorder,
        capBezierPoints: false,
      },
      point: {
        radius: width >= 1920 || isMobile ? 5 : chartBorder,
        hoverRadius: width >= 1920 || isMobile ? 6 : chartBorder + 0.5,
        borderWidth: 0,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "white",
        titleColor: "#414040",
        bodyColor: "#414040",
        usePointStyles: true,
        borderColor: "rgba(247, 207, 221, 0.80)",
        padding: width >= 1920 || isMobile ? 20 : chartPadding,
        caretPadding: width >= 1920 || isMobile ? 20 : chartPadding,
        cornerRadius: width >= 1920 || isMobile ? 20 : chartPadding,
        boxPadding: width >= 1920 || isMobile ? 20 : chartPadding,
        usePointStyle: true,
        borderWidth: width >= 1920 || isMobile ? 2 : chartBorder - 1.5,
        titleAlign: "center",
        bodyAlign: "right",
        titleFont: {
          size: width >= 1920 || isMobile ? 16 : chartFontSize,
          family: "'IBM Plex Sans', sans-serif",
        },
        bodyFont: {
          size: width >= 1920 || isMobile ? 14 : chartFontSize,
          family: "'IBM Plex Sans', sans-serif",
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#414040",
          font: {
            size: width >= 1920 || isMobile ? 14 : chartFontSize,
            family: "'IBM Plex Sans', sans-serif",
          },
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#414040",
          font: {
            size: width >= 1920 || isMobile ? 14 : chartFontSize,
            family: "'IBM Plex Sans', sans-serif",
          },
        },
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        grid: {
          display: false,
        },
        ticks: {
          color: "#414040",
          font: {
            size: width >= 1920 || isMobile ? 14 : chartFontSize,
            family: "'IBM Plex Sans', sans-serif",
          },
        },
      },
    },
  };

  const doughnutOptions = {
    cutout: width >= 1920 || isMobile ? 80 : chartCutout,
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 3000,
    },
    plugins: {
      legend: {
        position: isMobile ? "bottom" : "right",
        labels: {
          boxWidth: width >= 1920 || isMobile ? 20 : chartFontSize,
          boxHeight: width >= 1920 || isMobile ? 20 : chartFontSize,
          padding: width >= 1920 || isMobile ? 16 : chartFontSize,
          usePointStyle: true,
          color: "#414040",
          font: {
            size: width >= 1920 || isMobile ? 14 : chartFontSize,
            family: "'IBM Plex Sans', sans-serif",
          },
        },
      },
      tooltip: {
        backgroundColor: "white",
        titleColor: "#414040",
        bodyColor: "#414040",
        usePointStyles: true,
        borderColor: "rgba(247, 207, 221, 0.80)",
        padding: width >= 1920 || isMobile ? 20 : chartPadding,
        caretPadding: width >= 1920 || isMobile ? 20 : chartPadding,
        cornerRadius: width >= 1920 || isMobile ? 20 : chartPadding,
        boxPadding: width >= 1920 || isMobile ? 20 : chartPadding,
        usePointStyle: true,
        borderWidth: width >= 1920 || isMobile ? 2 : chartBorder - 1.5,
        titleAlign: "center",
        bodyAlign: "right",
        titleFont: {
          size: width >= 1920 || isMobile ? 16 : chartFontSize,
          family: "'IBM Plex Sans', sans-serif",
        },
        bodyFont: {
          size: width >= 1920 || isMobile ? 14 : chartFontSize,
          family: "'IBM Plex Sans', sans-serif",
        },
      },
    },
  };

  const onSetDate = async (key, date) => {
    const isoValue = date instanceof Date ? date.toISOString() : date;
    const newDate = {...chartsDate, [key]: isoValue};

    await dispatch(setChartsDate(newDate));
    await dispatch(
      getSessionsByMonth({
        fromDate: newDate.from,
        toDate: newDate.to,
        withArchived,
      }),
    );
    await dispatch(
      getSessionsByScore({
        fromDate: newDate.from,
        toDate: newDate.to,
        withArchived,
      }),
    );
  };

  const validScores = chartAvgScore
    .filter((score) => score !== null)
    .map(Number);
  const avgScorePeriod = validScores.length
    ? validScores.reduce((a, b) => a + b, 0) / validScores.length
    : 0;
  const safeAvgScore = Math.max(0, Math.min(avgScorePeriod, 5));
  const minAngle = -90;
  const maxAngle = 90;
  const percent = safeAvgScore / 5;
  const arrowAngle = minAngle + percent * (maxAngle - minAngle);

  const conductedTemplate = t("page.dashboard.conducted", {count: "___"});
  const [beforeCount, afterCount] = conductedTemplate.split("___");

  const isLoading = status === "loading";
  const isError = status === "error";
  const isEmpty =
    (!data?.month ||
      !data?.month.labels ||
      data?.month.labels.length === 0 ||
      !data?.score ||
      !data?.score.raw ||
      data?.score.raw.length === 0) &&
    status === "success";
  const hasData =
    status === "success" &&
    data?.month &&
    data?.month.labels &&
    data?.month.labels.length > 0 &&
    data?.score &&
    data?.score.raw &&
    data?.score.raw.length > 0;

  const updateCharts = async () => {
    await dispatch(
      getSessionsByMonth({
        fromDate: chartsDate.from,
        toDate: chartsDate.to,
        withArchived,
      }),
    );
    await dispatch(
      getSessionsByScore({
        fromDate: chartsDate.from,
        toDate: chartsDate.to,
        withArchived,
      }),
    );
  };

  useEffect(() => {
    updateCharts();
  }, [withArchived]);

  return (
    <div className={styles.charts}>
      <Head
        title={<span>{t("page.dashboard.visit_chart_title")}</span>}
        checkboxLabel={t("page.dashboard.checkbox_filter")}
        setWithArchived={setWithArchived}
        withArchived={withArchived}
        chartsDate={chartsDate}
        minDate={minDate}
        setFromDate={(date) => onSetDate("from", date)}
        setToDate={(date) => onSetDate("to", date)}
      />

      {isLoading && (
        <Spinner
          width={80}
          height={80}
        />
      )}

      {isError && (
        <WarningData
          type={"error"}
          text={error}
        />
      )}

      {isEmpty && <WarningData type={"empty"} />}

      {hasData && (
        <div className={styles.chartsWrapper}>
          <div className={styles.chartsRootWrapper}>
            <div className={styles.chartsRoot}>
              <Chart
                type={"bar"}
                options={rootOptions}
                data={rootData}
              />
            </div>

            <ChartLabel labelsList={doughnutLabelsList} />
          </div>

          <div className={styles.chartsDoughnutWrapper}>
            <div className={styles.chartsDoughnut}>
              <div className={styles.chartsDoughnutTitle}>
                <RootDesc>
                  <span>
                    {beforeCount}
                    <strong>
                      <CountUp
                        end={totalSimulations}
                        duration={3}
                        preserveValue
                      />
                    </strong>
                    {afterCount}
                  </span>
                </RootDesc>
              </div>

              <Doughnut
                data={doughnutData}
                options={doughnutOptions}
              />
            </div>

            <div className={styles.chartsSpeedometer}>
              <div className={styles.chartsSpeedometerTitle}>
                <RootDesc>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t("page.dashboard.period_score"),
                    }}
                  />
                </RootDesc>
              </div>

              <div className={styles.chartsSpeedometerImages}>
                <img
                  className={styles.chartsSpeedometerArch}
                  src={radarChart}
                  alt={"radar"}
                />

                {chartAvgScore.length > 0 && (
                  <>
                    <div
                      className={styles.chartsSpeedometerBody}
                      style={{transform: `rotate(${arrowAngle}deg)`}}
                    >
                      <img
                        className={styles.chartsSpeedometerArrow}
                        src={radarArrow}
                        alt={"radar-arrow"}
                      />
                      <img
                        className={styles.chartsSpeedometerCircle}
                        src={radarBody}
                        alt={"radar-body"}
                      />
                    </div>
                    <div className={styles.chartsSpeedometerChartTitle}>
                      <CountUp
                        end={safeAvgScore && Number(safeAvgScore).toFixed(1)}
                        decimals={1}
                        duration={3}
                        preserveValue
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
